import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import update from 'immutability-helper';

import Loading from '../../../shared/loading';
import defaultOnErrorHandler from '../../../shared/default-on-error-handler';
import Validator from '../../../shared/validator';
import SingleDatePickerWrapper from '../../../shared/single-date-picker-wrapper';
import LivewireEvent from './livewire-event';

export default class EditLivewireEventModal extends React.Component {
  static propTypes = {
    eventDidSave: PropTypes.func.isRequired,
  }

  constructor(props, context) {
    super(props, context);

    this.validator = new Validator({
      nameRequired: state => !!state.event.name,
      slugRequired: state => !!state.event.slug,
      startsAtRequired: state => !!state.event.starts_at,
      eventbrite_idRequired: state => !!state.event.eventbrite_id,
      cityRequired: state => !!state.event.city,
      starts_at_timeRequired: state => !!state.event.starts_at_time,
      starts_at_timeFormat: state => !state.event.starts_at_time || moment(`1900-01-01T${this.state.event.starts_at_time}`).isValid(),
    });
    this.state = {
      event: new LivewireEvent(),
      slugHasBeenManuallyChanged: false,
      validations: this.validator.getValidations(),
    };
  }

  componentDidMount() {
    const self = this;

    this.$modal.click(function onClick(e) {
      if (e.target === this) self.dismiss();
    });
  }

  handleNameChange(e) {
    const name = e.target.value;

    this.setState(state => {
      state.event.name = name;
      this.validate(state, 'nameRequired');

      if (!state.slugHasBeenManuallyChanged) {
        state.event.slug = _.snakeCase(name);
        this.validate(state, 'slugRequired');
      }
      return state;
    });
  }

  handleSlugChange(e) {
    const slug = e.target.value;

    this.setState(state => {
      state.slugHasBeenManuallyChanged = !!slug;
      state.event.slug = slug;
      this.validate(state, 'slugRequired');
      return state;
    });
  }

  handleStartsAtChange(startsAt) {
    this.setState(state => {
      state.event.starts_at = startsAt;
      this.validate(state, 'startsAtRequired');
      return state;
    });
  }

  handleStartsAtTimeChange(e) {
    const startsAtTime = e.target.value;

    this.setState(state => {
      state.event.starts_at_time = startsAtTime;
      this.validate(state, 'starts_at_timeFormat', 'starts_at_timeRequired');
      return state;
    });
  }

  handleEventbriteIdChange(e) {
    const eventbriteId = e.target.value;

    this.setState(state => {
      state.event.eventbrite_id = eventbriteId;
      this.validate(state, 'eventbrite_idRequired');
      return state;
    });
  }

  handleRolloverEventbriteIdChange(e) {
    const rolloverEventbriteId = e.target.value;

    this.setState(state => update(state, {
      event: {
        rollover_eventbrite_id: { $set: rolloverEventbriteId },
      },
    }));
  }

  handleCityChange(e) {
    const city = e.target.value;

    this.setState(state => {
      state.event.city = city;
      this.validate(state, 'cityRequired');
      return state;
    });
  }

  handleHiddenChange(e) {
    const hidden = e.target.checked;

    this.setState(state => update(state, {
      event: {
        hidden: { $set: hidden },
      },
    }));
  }

  handleIncludeDigitalPassChange(e) {
    const includeDigitalPass = e.target.checked;

    this.setState(state => update(state, {
      event: {
        include_digital_pass: { $set: includeDigitalPass },
      },
    }));
  }

  validate(state, ...rules) {
    _.each(rules, r => {
      const validatorName = `validate${r[0].toUpperCase()}${r.slice(1)}`;
      state.validations[`${r}Valid`] = this.validator[validatorName](state);
    });
  }

  save(e) {
    e && e.preventDefault && e.preventDefault();

    this.setState(
      state => ({
        validations: this.validator.validateForm(state),
      }),
      () => {
        if (!this.validator.formValid) return;

        this.loading.start();
        this.state.event.save()
          .then(result => {
            const event = new LivewireEvent(result.data);

            this.loading.stop();
            this.dismiss();
            this.props.eventDidSave && this.props.eventDidSave(event);
          })
          .catch(error => {
            this.loading.stop();
            defaultOnErrorHandler(error, 'Error saving event');
          });
      },
    );
  }

  present(event) {
    this.validator.resetValidation();
    this.setState({
      event: event || new LivewireEvent(),
      slugHasBeenManuallyChanged: false,
      validations: this.validator.getValidations(),
    });
    this.$overlay.fadeIn();
    this.$modal.fadeIn();
    this.$eventNameInput.focus();
  }

  dismiss() {
    this.$overlay.fadeOut();
    this.$modal.fadeOut();
  }

  renderName() {
    return (
      <p className={`form-group ${this.state.validations.nameRequiredValid ? '' : 'has-error'}`}>
        <label htmlFor="name">
          Event name:
          <input
            id="name"
            type="text"
            placeholder="Event name"
            ref={el => (this.$eventNameInput = $(el))}
            value={this.state.event.name}
            onChange={e => this.handleNameChange(e)}
          />
        </label>
        {this.state.validations.nameRequiredValid || <span className="help-block">Event name is required</span>}
      </p>
    );
  }

  renderSlug() {
    return (
      <p className={`form-group ${this.state.validations.slugRequiredValid ? '' : 'has-error'}`}>
        <label htmlFor="slug">
          Event slug:
          <input
            id="slug"
            type="text"
            placeholder="Event slug"
            value={this.state.event.slug}
            onChange={e => this.handleSlugChange(e)}
          />
        </label>
        {this.state.validations.slugRequiredValid || <span className="help-block">Event slug is required</span>}
        <span className="info-block">The event slug must match the Livewire page for the event. See your IT team for assistance.</span>
      </p>
    );
  }

  renderDate() {
    /* eslint-disable jsx-a11y/label-has-associated-control */
    return (
      <div className={`form-group ${this.state.validations.startsAtRequiredValid ? '' : 'has-error'}`}>
        <label htmlFor="date">
          Starts at:
          <SingleDatePickerWrapper
            id="date"
            initialDate={this.state.event.starts_at}
            onDateChange={date => this.handleStartsAtChange(date)}
          />
        </label>
        {this.state.validations.startsAtRequiredValid || <span className="help-block">The starting date/time is required</span>}
      </div>
    );
    /* eslint-enable jsx-a11y/label-has-associated-control */
  }

  renderTime() {
    return (
      <div className={`form-group ${this.state.validations.starts_at_timeFormatValid && this.state.validations.starts_at_timeRequiredValid ? '' : 'has-error'}`}>
        <label htmlFor="time">
          Time:
          <input
            id="time"
            type="text"
            placeholder="HH:MM"
            value={this.state.event.starts_at_time}
            onChange={e => this.handleStartsAtTimeChange(e)}
          />
        </label>
        {this.state.validations.starts_at_timeRequiredValid || <span className="help-block">Starts at time is required</span>}
        {this.state.validations.starts_at_timeFormatValid || <span className="help-block">Starts at time must be in the correct format (HH:MM, 24 hour time)</span>}
      </div>
    );
  }

  renderEventBrite() {
    return (
      <p className={`form-group ${this.state.validations.eventbrite_idRequiredValid ? '' : 'has-error'}`}>
        <label htmlFor="event-brite">
          Eventbrite ID:
          <input
            id="event-brite"
            type="text"
            placeholder="Eventbrite ID"
            value={this.state.event.eventbrite_id}
            onChange={e => this.handleEventbriteIdChange(e)}
          />
        </label>
        {this.state.validations.eventbrite_idRequiredValid || <span className="help-block">The Eventbrite ID is required</span>}
      </p>
    );
  }

  renderRollOver() {
    return (
      <p className="form-group">
        <label htmlFor="roll-over">
          Rollover Eventbrite ID:
          <input
            id="roll-over"
            type="text"
            placeholder="Rollover Eventbrite ID"
            value={this.state.event.rollover_eventbrite_id}
            onChange={e => this.handleRolloverEventbriteIdChange(e)}
          />
        </label>
      </p>
    );
  }

  renderCity() {
    return (
      <p className={`form-group ${this.state.validations.cityRequiredValid ? '' : 'has-error'}`}>
        <label htmlFor="city">
          City:
          <input
            id="city"
            type="text"
            placeholder="City"
            value={this.state.event.city}
            onChange={e => this.handleCityChange(e)}
          />
        </label>
        {this.state.validations.cityRequiredValid || <span className="help-block">The city is required</span>}
      </p>
    );
  }

  renderDigitalPass() {
    return (
      <p className="form-group">
        <label htmlFor="digital-pass">
          <input
            id="digital-pass"
            type="checkbox"
            checked={this.state.event.include_digital_pass}
            onChange={e => this.handleIncludeDigitalPassChange(e)}
          />
          Include Digital Pass
        </label>
      </p>
    );
  }

  renderHidden() {
    return (
      <p className="form-group">
        <label htmlFor="hidden-checkbox">
          <input
            id="hidden-checkbox"
            type="checkbox"
            checked={this.state.event.hidden}
            onChange={e => this.handleHiddenChange(e)}
          />
          Hidden
        </label>
      </p>
    );
  }

  render() {
    return (
      <div>
        <div className="modal-overlay" role="region" ref={el => (this.$overlay = $(el))} onClick={() => this.dismiss()} />
        <div className="modal" tabIndex="-1" role="dialog" ref={el => (this.$modal = $(el))}>
          <article>
            <header>
              <h4>
                <span>{`${this.state.event.id ? 'Editing' : 'Creating'} an event`}</span>
              </h4>
            </header>
            <Loading ref={c => (this.loading = c)}>
              <section>
                <form>
                  {this.renderName()}
                  {this.renderSlug()}
                  {this.renderDate()}
                  {this.renderTime()}
                  {this.renderEventBrite()}
                  {this.renderRollOver()}
                  {this.renderCity()}
                  {this.renderDigitalPass()}
                  {this.renderHidden()}
                </form>
              </section>
              <footer>
                <button type="button" className="btn btn-secondary" onClick={() => this.dismiss()}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={e => this.save(e)}>Save</button>
              </footer>
            </Loading>
          </article>
        </div>
      </div>
    );
  }
}
